import React, { ReactNode } from 'react';
import { v4 as uuidv4 } from 'uuid';

import { IconCheck, IconFioriNotification } from '../../icons/v1';
import { BusyIndicator } from '../BusyIndicator/BusyIndicator';
import { Button, ButtonCategory, ButtonProps } from '../LegacyButton';

type LoadingStatus = 'not-started' | 'is-loading' | 'succeeded' | 'failed';

interface AnimatedLoadingButtonProps extends Omit<
  ButtonProps,
  'icon' | 'iconPosition' | 'category'
> {
  /**
   * The loading status of the button.
   *
   * @default 'not-started'
   */
  status?: LoadingStatus;
}

const categoryByLoadingStatus = new Map<LoadingStatus, ButtonCategory>([
  ['not-started', 'default'],
  ['is-loading', 'default'],
  ['succeeded', 'success'],
  ['failed', 'danger'],
]);

const iconByLoadingStatus = new Map<LoadingStatus, ReactNode>([
  ['not-started', null],
  [
    'is-loading',
    <BusyIndicator
      key={uuidv4()}
      size="S"
      style={{ height: '100%' }}
    />,
  ],
  ['succeeded', <IconCheck key={uuidv4()} />],
  ['failed', <IconFioriNotification key={uuidv4()} />],
]);

export function AnimatedLoadingButton({
  children,
  className,
  disabled,
  external,
  href,
  onClick,
  onMouseDown,
  onMouseUp,
  openInNewTab,
  size,
  status = 'not-started',
  style,
  type,
  variant,
}: AnimatedLoadingButtonProps) {
  const category = categoryByLoadingStatus.get(status) || 'default';
  const icon = iconByLoadingStatus.get(status);
  const shouldHandleUserInput = status === 'not-started';

  return (
    <Button
      category={category}
      className={className}
      disabled={disabled}
      external={external}
      href={href}
      icon={icon}
      iconPosition="left"
      onClick={shouldHandleUserInput ? onClick : undefined}
      onMouseDown={shouldHandleUserInput ? onMouseDown : undefined}
      onMouseUp={shouldHandleUserInput ? onMouseUp : undefined}
      openInNewTab={openInNewTab}
      size={size}
      style={style}
      type={type}
      variant={variant}
    >
      {children}
    </Button>
  );
}
